import { useState } from 'react'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import { CommentOutlined } from '@mui/icons-material'
import { apiPut } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import { withExtendedColumnsAfterDescription } from '../workflow2.0/workflowSelectionColumns'
import WorkflowLastSaveCommentDialog from '../workflow2.0/WorkflowLastSaveCommentDialog'

/**
 * Paginated table of approved workflows (with their other versions in an
 * expandable accordion row) used by the workflow selector dialog in
 * `BalancedHeader`.
 *
 * @param {Object} props
 * @param {function(Object): void} props.onWorkflowSelected - Called with the workflow row when a row is clicked
 * @returns {JSX.Element}
 */
const WorkflowTable = ({ onWorkflowSelected }) => {
  const { t_ } = useSmartTranslation()
  const [commentWorkflowId, setCommentWorkflowId] = useState(0)
  // Story 188 (RN1-RN4): the active extended fields of the workflow are columns of this table.
  const extendedColumns = useGetExtendedColumns('workflow')

  // The table mounts ONCE, with its final columns: remounting it costs a request per mount.
  if (extendedColumns === undefined) {
    return <CircularProgress />
  }

  // Story 188 (RN3b): extended fields between Description and Balanced. `balanceMode` is computed
  // when the response is built (RN22): never a search or filter column. E1 (RN24): Code goes first.
  const columns = withExtendedColumnsAfterDescription([
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Part reference'),
      field: 'partReference.name'
    },
    {
      name: t_('Name'),
      field: 'name'
    },
    {
      name: t_('Version'),
      field: 'version'
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Balanced'),
      field: 'balanceMode',
      type: 'boolean',
      disableGenericSearch: true,
      disableFilter: true
    }
  ], extendedColumns)

  /**
   * Fetches the approved versions of a given workflow (rendered inside the
   * accordion row).
   *
   * @param {number} id - Workflow id whose versions are requested
   * @param {Object} pagination - Pagination / sort payload from the table
   * @returns {Promise<Object>} Paged result; each item gets `moreVersions: false`
   */
  const dataFetchVersions = async (id, pagination) => {
    const result = await apiPut('getAllApprovedWorkflowsByWorkflowId/' + id, pagination)
    if (result.content) {
      result.content = result.content.map(element => ({
        ...element,
        moreVersions: false
      }))
    }
    return result
  }
  const actions = {
    accordion: {
      visibilityButton: (row) => {
        return row.moreVersions
      },
      render: (row) => {
        if (!row.moreVersions) {
          return null
        }
        // No tune key here (RN11): the accordion always shows every column and keeps its 3 rows.
        return (
          <SmartMateriaUITable
            actions={actions}
            columns={columns}
            sticky={false}
            dataFetch={(pagination) => dataFetchVersions(row.versionOf, pagination)}
            toolbarDisplay='none'
            dense
            onRowClick={(row) => onWorkflowSelected(row)}
            tablePageSize={3}
            pageable
            safeDeleteShowProperty='name'
            sortOptions={{
              default: {
                field: 'version',
                direction: 1
              }
            }}
          />
        )
      }
    },
    custom: [
      {
        // Story 188 (RN21): a row button, no three-dots menu. `contextMenu: false` makes the table
        // paint `render` directly in the row, so the click (and its propagation to the row) is ours.
        name: t_('Show comment'),
        contextMenu: false,
        render: (row) => (
          <Tooltip title={t_('Show comment')}>
            <IconButton
              aria-label={t_('Show comment')}
              onClick={(event) => {
                event.stopPropagation()
                setCommentWorkflowId(row.id)
              }}
            >
              <CommentOutlined />
            </IconButton>
          </Tooltip>
        )
      }
    ]
  }

  /**
   * Fetches the top-level page of approved workflow versions for the table.
   *
   * @param {Object} pagination - Pagination / sort payload from the table
   * @returns {Promise<Object>} Paged result
   */
  const fetchData = async (pagination) => {
    return await apiPut('getAllApprovedWorkflowVersions', pagination)
  }

  return (
    <div>
      <WorkflowLastSaveCommentDialog workflowId={commentWorkflowId} onClose={() => setCommentWorkflowId(0)} />
      <SmartMateriaUITable
        tableTuneUniqueKey='balancedWorkflowSelection'
        actions={actions}
        columns={columns}
        sticky={false}
        dataFetch={fetchData}
        onRowClick={(row) => onWorkflowSelected(row)}
        pageable
        sortOptions={{
          default: {
            field: 'name',
            direction: 1
          }
        }}
      />
    </div>
  )
}

export default WorkflowTable
